/*****************************************************************
 *  CUSTOMER BUTLER - A RECORD'S SECTIONS (Zoho's arrangement)     *
 *  File: apps/web/src/pages/customer/RecordSections.js            *
 *  What it does: draws a record's sections exactly as the layout  *
 *  door sends them - Zoho's section titles in Zoho's order, every *
 *  field in its place, blanks shown as blanks - and lets a person *
 *  change one field in place (HR Butler's tap-the-value pattern). *
 *  Reads: the record and the layout it is handed.                 *
 *  Writes: through the onSaveField handler it is handed (the      *
 *  record page sends the change to the record's own door).        *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - ONE FACT (label above value)                       *
 *  SECTION 2 - ONE FACT BEING EDITED                              *
 *  SECTION 3 - ONE SECTION, AND ALL OF THEM                       *
 *  SECTION 4 - SENDING ONE CHANGED FIELD (shared by every record) *
 *****************************************************************/

import React, { useCallback, useState } from 'react';
import { Icon } from './customerIcons';
import { FieldEditor } from './FieldEditor';
import { useCustomerAccessView } from './customerAccessView';
import { readFieldValue, showFieldValue, startingEditValue, buildSaveBody } from './fieldValues';

/*****************************************************************
 *  SECTION 1 - ONE FACT                                           *
 *****************************************************************/

const BLANK_MARK = '—'; // the quiet dash a blank shows, like Zoho's empty field

/* ---- FactValue({ screenField, shownWords, onStartEdit }) -------
   The value: a button that reads as text when it may be changed,
   plain text when it may not.                                   */
function FactValue({ screenField, shownWords, onStartEdit }) {
  const isBlank = shownWords === '';
  const valueWords = isBlank ? BLANK_MARK : shownWords;
  const valueClass = isBlank ? 'cb-fact-value is-blank' : 'cb-fact-value';

  if (!screenField.canEdit) {
    return <dd className={valueClass}>{valueWords}</dd>;
  }

  return (
    <dd className={valueClass}>
      <button
        type="button"
        className="cb-value-btn"
        onClick={onStartEdit}
        aria-label={`Change ${screenField.label}`}
      >
        <span className="cb-value-text">{valueWords}</span>
        <span className="cb-value-pen"><Icon name="pencil" /></span>
      </button>
    </dd>
  );
}

/*****************************************************************
 *  SECTION 2 - ONE FACT BEING EDITED                              *
 *****************************************************************/

/* ---- FactEditor({ screenField, startValue, onSave, onCancel }) --
   The open box, Save and Cancel, and the server's refusal in
   words if it says no. onSave returns { ok, error }.            */
function FactEditor({ screenField, startValue, onSave, onCancel }) {
  const [draftValue, setDraftValue] = useState(startValue);
  const [isSaving, setIsSaving] = useState(false);
  const [refusalWords, setRefusalWords] = useState('');
  const inputId = `cb-edit-${screenField.kind}-${screenField.key}`;

  async function saveDraft(submitEvent) {
    submitEvent.preventDefault();
    setIsSaving(true);
    setRefusalWords('');

    const saveAnswer = await onSave(draftValue);

    setIsSaving(false);

    if (!saveAnswer.ok) {
      setRefusalWords(saveAnswer.error || 'That did not save. Nothing was changed.');
    }
  }

  return (
    <dd className="cb-fact-value">
      <form className="cb-edit" onSubmit={saveDraft}>
        <label className="cb-hidden-label" htmlFor={inputId}>{screenField.label}</label>
        <FieldEditor screenField={screenField} value={draftValue} onChange={setDraftValue} inputId={inputId} />
        {refusalWords ? <p className="cb-edit-error" role="alert">{refusalWords}</p> : null}
        <div className="cb-edit-acts">
          <button type="submit" className="bc-btn is-primary" disabled={isSaving}>
            {isSaving ? 'Saving' : 'Save'}
          </button>
          <button type="button" className="bc-btn is-ghost" onClick={onCancel} disabled={isSaving}>
            Cancel
          </button>
        </div>
      </form>
    </dd>
  );
}

/* ---- Fact({ record, screenField, onSaveField }) ----------------
   One field: its label, then either the value or the open box.  */
function Fact({ record, screenField, onSaveField }) {
  const [isEditing, setIsEditing] = useState(false);
  const accessView = useCustomerAccessView(); // Look up = read only (Honest UI, 2026-10-14)
  const mayEditThisField = accessView.mayWrite && screenField.canEdit;
  const rawValue = readFieldValue(record, screenField);
  const shownWords = showFieldValue(screenField, rawValue);

  async function saveOneValue(newValue) {
    const saveBody = buildSaveBody(screenField, newValue);
    const saveAnswer = await onSaveField(saveBody);

    if (saveAnswer.ok) {
      setIsEditing(false);
    }

    return saveAnswer;
  }

  return (
    <div className="cb-fact">
      <dt className="cb-fact-label">{screenField.label}</dt>
      {isEditing && mayEditThisField ? (
        <FactEditor
          screenField={screenField}
          startValue={startingEditValue(screenField, rawValue)}
          onSave={saveOneValue}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <FactValue
          screenField={{ ...screenField, canEdit: mayEditThisField }}
          shownWords={shownWords}
          onStartEdit={() => setIsEditing(true)}
        />
      )}
    </div>
  );
}

/*****************************************************************
 *  SECTION 3 - ONE SECTION, AND ALL OF THEM                       *
 *****************************************************************/

/* ---- sentenceCaseTitle(title) ----------------------------------
   Zoho has one section named "import" in lower case; titles start
   with a capital (sentence case), the words are kept as Zoho's.  */
export function sentenceCaseTitle(title) {
  const titleText = String(title || '');

  return titleText.charAt(0).toUpperCase() + titleText.slice(1);
}

/* ---- SectionFacts({ record, section, onSaveField }) -------------
   The facts of one section, in the layout's order (no card around
   them). The clean look wraps them in its section card; the HR
   look puts them inside one of HR's folded cards (2026-10-15).  */
export function SectionFacts({ record, section, onSaveField }) {
  return (
    <dl className="cb-facts">
      {section.fields.map((screenField) => (
        <Fact
          key={`${screenField.kind}-${screenField.key}`}
          record={record}
          screenField={screenField}
          onSaveField={onSaveField}
        />
      ))}
    </dl>
  );
}

/* ---- RecordSection({ record, section, onSaveField }) ----------- */
function RecordSection({ record, section, onSaveField }) {
  return (
    <details className="cb-section" open>
      <summary>
        {sentenceCaseTitle(section.title)}
        <span className="cb-section-caret"><Icon name="chevron" /></span>
      </summary>
      <SectionFacts record={record} section={section} onSaveField={onSaveField} />
    </details>
  );
}

/* ---- RecordSections({ record, sections, onSaveField }) ---------
   In: the record; layout.sections; onSaveField(saveBody) that
   returns { ok, error } after the door answers.
   Out: every section, in the layout's order.                    */
export function RecordSections({ record, sections, onSaveField }) {
  return (
    <div>
      {sections.map((oneSection) => (
        <RecordSection key={oneSection.title} record={record} section={oneSection} onSaveField={onSaveField} />
      ))}
    </div>
  );
}

/*****************************************************************
 *  SECTION 4 - SENDING ONE CHANGED FIELD                          *
 *  One copy of the rule, used by the customer and property        *
 *  records in both the clean and the HR look (2026-10-15).        *
 *****************************************************************/

const DID_NOT_SAVE_WORDS = 'That did not save. Nothing was changed.'; // when the door gives no reason

/* ---- useSaveOneField(saveToDoor, recordId, setRecord) ------------
   In: the record's save door (saveCustomer / saveProperty), the
   record id, and the page's setRecord.
   Out: saveOneField(saveBody) -> { ok } or { ok:false, error }.
   On a yes, the saved columns replace the shown ones; what the
   door does not send back (properties, contacts, owner) is kept. */
export function useSaveOneField(saveToDoor, recordId, setRecord) {
  return useCallback(async (saveBody) => {
    const saveAnswer = await saveToDoor(recordId, saveBody);

    if (!saveAnswer.ok) {
      const refusal = saveAnswer.error || {};
      const detailWords = Array.isArray(refusal.details) ? refusal.details.join(' ') : '';

      return { ok: false, error: detailWords || refusal.message || DID_NOT_SAVE_WORDS };
    }

    setRecord((recordNow) => ({ ...recordNow, ...saveAnswer.data }));

    return { ok: true };
  }, [saveToDoor, recordId, setRecord]);
}

export default RecordSections;
